import React, {useCallback, useEffect, useState} from "react";
import {memo} from "react";
import * as _ from "lodash";
import Dialog from "../Dialogs/Dialog";
import {GridApi} from "ag-grid-community";
import i18next from "i18next";
import TextFieldRegEx from "../TextFieldRegEx";
import {RegexFactory} from "src/services/ColumnFactory";
import SelectField from "../SelectField";
import {CnrDto, KumaCnrByRegionParams, KumaViewDto, ModeltypeBasicDto, RegionDto, TypefamilyDto} from "src/services/Client";
import DataAdapterKumaView from "src/views/KumaView/DataAdapterKumaView";
import HATEOASClient from "src/services/HATEOASClient";
import Button from "../Button";
import {Table} from "semantic-ui-react";
import {renderErrorToast} from "src/utils/FeedbackMessageUtils";
import {derivateRenderer, modelYearsRender, typefamilyRenderer} from "src/views/KumaView/NewKumaViewDialog";
import {constants} from "os";

interface IOwnProps {
    data: any;
    api: GridApi;
    t: i18next.TranslationFunction<any, object, string>;
    kumaExtraLinks?: any;
    userCnrs?: any;
    client?: HATEOASClient;
    parentProps: any;

}

export const DerivateEdit = memo((props: IOwnProps) => {
    const {data, api, t} = props;
    const [open, setOpen] = useState(true);
    const [upItem, setUpItem] = useState({...props.data});
    const [derivates, setDerivates] = useState<any>(null);
    const [filteredDerivates, setFilteredDerivates] = useState([]);
    const {createNewRequirementLink, client, onRowUpdate} = props.parentProps;

    const getFilterDerivateFromNewItem = useCallback(
        (newItem: any) => {
            const selectedTypeFamiliesNames = newItem.typefamilies.map((item: any) => item.name.trim());
            const selectedModelYearsObjIds = newItem.modelyears.map((item: any) => item.objectId);
            if (selectedModelYearsObjIds.length > 0 && selectedTypeFamiliesNames.length > 0) {
                const getDerivateBasedOnTypeFamily = derivates.filter((derivate: any) => selectedTypeFamiliesNames.includes(derivate.typefamily.name.trim()));
                const getDerivateBasedOnModelYear = getDerivateBasedOnTypeFamily.filter((derivate: any) => selectedModelYearsObjIds.indexOf(derivate.modelyear.objectId) !== -1);
                return getDerivateBasedOnModelYear;
            } else if (selectedTypeFamiliesNames.length > 0 && selectedModelYearsObjIds.length === 0) {
                const getDerivateBasedOnTypeFamily = derivates.filter((derivate: any) => selectedTypeFamiliesNames.includes(derivate.typefamily.name.trim()));
                return getDerivateBasedOnTypeFamily;
            } else if (selectedModelYearsObjIds.length > 0 && selectedTypeFamiliesNames.length === 0) {
                const getDerivateBasedOnModelYear = derivates.filter((derivate: any) => selectedModelYearsObjIds.indexOf(derivate.modelyear.objectId) !== -1);
                return getDerivateBasedOnModelYear;
            }
            return derivates;
        },
        [derivates]);

    const loadData = useCallback(
        async () => {
            if (createNewRequirementLink) {
                try {
                    const response = await client.GET_kuma_dertivates(createNewRequirementLink).execute();
                    setDerivates(response.elements);
                } catch (error) {
                    console.error("Error loading derivates:", error);
                }
            }
        },
        [client, createNewRequirementLink]);

    useEffect(
        () => {
            loadData();
        },
        [loadData]);

    useEffect(
        () => {
            if (derivates && derivates.length > 0) {
                setFilteredDerivates(getFilterDerivateFromNewItem(upItem));
            }
        },
        [derivates, upItem]
    );

    const handleTypeFamilyChange = (selectedValue: any[]) => {
        const updatedItem = {...upItem, typefamilies: selectedValue!.map((item: any) => item.selectedValue)};
        setUpItem(updatedItem);
    };

    const handleModelYearChange = (selectedValue: any[]) => {
        const updatedItem = {...upItem, modelyears: selectedValue!.map((item: any) => item.selectedValue)};
        setUpItem(updatedItem);
    };

    const handleDerivateChange = (selectedValue: any[]) => {
        setUpItem((prev: any) => ({...prev, derivates: selectedValue!.map((item: any) => item.selectedValue)}));
    };
    const handleUpdate = () => {
        onRowUpdate(upItem);
    };
    return (
        <Dialog
            title={`${t("KUMA_VIEW:KUMA_EDIT_DERIVATES")} - ${data.title}`}
            open={open}
            actions={[
                <Button
                    key={1}
                    icon={"checkmark"}
                    disabled={upItem && upItem.typefamilies.length === 0}
                    onClick={handleUpdate}
                />,
                <Button
                    key={"closeButton"}
                    icon={"ban"}
                    onClick={() => setOpen(false)}
                />
            ]}
        >
            <Table basic='very'>
                <Table.Body>
                    <Table.Row>
                        <Table.HeaderCell width={3}>{t("KUMA_VIEW:TYPEFAMILY")}<b>*</b></Table.HeaderCell>
                        <Table.Cell>
                            <SelectField
                                clearable={false}
                                multi={true}
                                dataSource={props.parentProps.typefamilies}
                                value={upItem.typefamilies}
                                rendererKey={(item: TypefamilyDto) => typefamilyRenderer(item, t)}
                                onChange={handleTypeFamilyChange}
                            />
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.HeaderCell width={3}>{t("KUMA_VIEW:MODELYEAR")}</Table.HeaderCell>
                        <Table.Cell>
                            {upItem.modelyears && <SelectField
                                clearable={false}
                                multi={true}
                                value={
                                    props.parentProps.userPSLModelyears
                                        .filter((masterItem: any) => upItem.modelyears.map((p: any) => p.objectId).includes(masterItem.objectId))
                                    || []
                                }
                                dataSource={props.parentProps.userPSLModelyears}
                                rendererKey={modelYearsRender}
                                onChange={handleModelYearChange}
                            />}
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.HeaderCell width={3}>{t("KUMA_VIEW:DERIVATES")}</Table.HeaderCell>
                        <Table.Cell>
                            {filteredDerivates && filteredDerivates.length > 0 && <SelectField
                                clearable={false}
                                multi={true}
                                value={derivates.filter((masterItem: any) => upItem.derivates.map((p: any) => p.objectId).includes(masterItem.objectId)) || []}
                                dataSource={filteredDerivates || []}
                                rendererKey={derivateRenderer}
                                onChange={handleDerivateChange}
                            />}
                        </Table.Cell>
                    </Table.Row>
                </Table.Body>
            </Table>
        </Dialog>
    );
});



// WEBPACK FOOTER //
// ./src/components/kuma/DerivateEdit.tsx